import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  ChangeDetectorRef, Component, ElementRef, Input, viewChild, ViewEncapsulation
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderStorageService } from 'render/render-storage/render-storage.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderStateService } from 'render/services/render-state/render-state.service';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { AssetsSharedService } from 'shared/services/assets-shared.service';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { SliceBaseHostStyles, SliceBaseRenderComponent } from 'extensions/core/slice-base/render/slice-base-render.component';
import { SliceMarqueeSettings } from 'extensions/core/slice-marquee/front/settings/slice-marquee-settings.model';
import { SLICE_MARQUEE_DEFAULT_SPEED } from 'extensions/core/slice-marquee/front/slice-marquee.definition';


@Component({
  selector: 'slice-marquee-render',
  templateUrl: './slice-marquee-render.component.html',
  styleUrls: ['./slice-marquee-render.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class SliceMarqueeRenderComponent extends SliceBaseRenderComponent {

  @Input() public slice!: IdtComponent<SliceMarqueeSettings>;
  public readonly swiperContainer = viewChild<ElementRef<SwiperContainer>>('swiper');

  public swiperConfig: SwiperOptions = {};
  public slidesRange: number[] = [];
  public singleSlide: number[] = [0, 0, 0, 0];
  public duration: number = 0;

  constructor(
    protected readonly domSanitizer: DomSanitizer,
    protected readonly assetsSharedService: AssetsSharedService,
    protected readonly breakpointObserver: BreakpointObserver,
    protected readonly idtSharedRenderService: IdtSharedRenderService,
    protected readonly renderConfigService: RenderConfigurationService,
    protected readonly cd: ChangeDetectorRef,
    protected readonly el: ElementRef<HTMLElement>,
    protected readonly renderStateService: RenderStateService,
    protected readonly renderStorageService: RenderStorageService,
    protected readonly renderSlugService: RenderSlugService,
    private readonly renderByDeviceService: RenderByDeviceService
  ) {
    super(
      domSanitizer,
      assetsSharedService,
      breakpointObserver,
      idtSharedRenderService,
      renderConfigService,
      cd,
      el,
      renderStateService,
      renderStorageService,
      renderSlugService
    );
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public speed(): number {
    if (this.isMobile) {
      return (this.slice.settings?.general.speedMobile ?? SLICE_MARQUEE_DEFAULT_SPEED) * 1000;
    }
    return (this.slice.settings?.general.speed ?? SLICE_MARQUEE_DEFAULT_SPEED) * 1000;
  }

  public stopAutoplay(): void {
    const swiper = this.swiperContainer()!.nativeElement.swiper;

    swiper.setTranslate(swiper.getTranslate());
    const currentSlideWidth = swiper.slides[swiper.activeIndex]?.offsetWidth;
    const distanceRatio = Math.abs(
      (currentSlideWidth * swiper.activeIndex + swiper.getTranslate())
      / currentSlideWidth
    );
    this.duration = swiper.params.speed! * distanceRatio;
    swiper.autoplay.stop();
  }

  public autoplayClick(): void {
    const swiper = this.swiperContainer()!.nativeElement.swiper;

    if (swiper?.autoplay) {
      if (swiper.autoplay.running) {
        this.stopAutoplay();
      } else {
        const distance = swiper.width * swiper.activeIndex + swiper.getTranslate();
        if (distance === 0) {
          this.duration = 0;
        }
        swiper.slideTo(swiper.activeIndex, this.duration);
        swiper.autoplay.start();
      }
    }
  }

  public getAutoplaylButtonAriaLabel(): string {
    if (this.swiperContainer()?.nativeElement?.swiper?.autoplay) {
      return this.swiperContainer()!.nativeElement.swiper.autoplay.running
        ? this.slice.settings?.general.accessibility?.buttonPauseText || ''
        : this.slice.settings?.general.accessibility?.buttonPlayText || '';
    }
    return '';
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.autoplayClick();
    }
  }

  protected onSliceInit(): void { /* empty */ }

  protected onMediaChange(breakpointState: BreakpointState): void {
    super.onMediaChange(breakpointState);

    this.initSlides();
    this.initSwiperConfig();
  }

  protected setDefaultHostStyles(): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings!.styles.bgColor || '',
      marginTop: this.slice.settings!.styles.marginTop || '',
      marginRight: this.slice.settings!.styles.marginRight || '',
      marginLeft: this.slice.settings!.styles.marginLeft || '',
      marginBottom: this.slice.settings!.styles.marginBottom || '',
      paddingTop: this.slice.settings!.styles.paddingTop,
      paddingRight: this.slice.settings!.styles.paddingRight,
      paddingLeft: this.slice.settings!.styles.paddingLeft,
      paddingBottom: this.slice.settings!.styles.paddingBottom
    });
  }

  protected setResponsiveHostStyles(
    breakpointStyles: ResponsiveSetting,
    defaultBpSettings: Partial<ResponsiveSetting>
  ): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings!.styles.bgColor || '',
      marginTop: breakpointStyles.marginTop || defaultBpSettings.marginTop || '',
      marginRight: breakpointStyles.marginRight || defaultBpSettings.marginRight || '',
      marginLeft: breakpointStyles.marginLeft || defaultBpSettings.marginLeft || '',
      marginBottom: breakpointStyles.marginBottom || defaultBpSettings.marginBottom || '',
      paddingTop: breakpointStyles.paddingTop || defaultBpSettings.paddingTop || '',
      paddingRight: breakpointStyles.paddingRight || defaultBpSettings.paddingRight || '',
      paddingLeft: breakpointStyles.paddingLeft || defaultBpSettings.paddingLeft || '',
      paddingBottom: breakpointStyles.paddingBottom || defaultBpSettings.paddingBottom || ''
    });
  }

  private initSlides(): void {
    this.slidesRange = Array.from({ length: 6 }, () => Object.keys(this.slice.children)
      .filter((childKey) => this.hasContent(this.slice.children[childKey][0]))
      .map((childKey) => +childKey - 1)).flat();
  }

  private initSwiperConfig(): void {
    this.swiperConfig = {
      autoHeight: false,
      loop: true,
      navigation: false,
      pagination: false,
      slidesPerView: 'auto',
      spaceBetween: 10,
      autoplay: {
        delay: 1,
        reverseDirection: this.slice.settings?.general.swipeDirection === 'right',
        disableOnInteraction: true
      },
      speed: this.speed(),
      allowTouchMove: false
    };
  }

  private setHostStyles(styles: SliceBaseHostStyles): void {
    this.backgroundColor = styles.backgroundColor;
    this.marginTop = styles.marginTop;
    this.marginRight = styles.marginRight;
    this.marginLeft = styles.marginLeft;
    this.marginBottom = styles.marginBottom;
    this.paddingTop = styles.paddingTop;
    this.paddingRight = styles.paddingRight;
    this.paddingLeft = styles.paddingLeft;
    this.paddingBottom = styles.paddingBottom;
  }

}
